// PowerBIEmbed.tsx
// Fetches an embed token from the backend (service-principal flow) and uses
// the Power BI JS SDK to embed the report — no user sign-in required.
// Falls back gracefully to a plain iframe (autoAuth=true) if the backend is
// not configured or the token request fails.

import React, { useEffect, useRef, useState } from 'react';
import * as pbi from 'powerbi-client';

interface PowerBIEmbedProps {
  url?: string;
  reportId: string;
  appId: string;
  ctid: string;
  title: string;
  isVisible: boolean;
  onLoad?: () => void;
}

// Singleton SDK instance — shared across all embedded reports
const powerbiService = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

const PowerBIEmbed: React.FC<PowerBIEmbedProps> = ({
  url,
  reportId,
  appId,
  ctid,
  title,
  isVisible,
  onLoad,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [embedState, setEmbedState] = useState<'idle' | 'loading' | 'embedded' | 'fallback'>('idle');
  const [gatewayIssue, setGatewayIssue] = useState(false);
  const embedRef = useRef<pbi.Embed | null>(null);
  const loadNotifiedRef = useRef(false);

  const markLoaded = () => {
    if (loadNotifiedRef.current) return;
    loadNotifiedRef.current = true;
    onLoad?.();
  };

  // Ensure filterPaneEnabled=true and navContentPaneEnabled=true are present in the fallback URL
  const buildFallbackUrl = (rawUrl?: string) => {
    const base = rawUrl || `https://app.powerbi.com/reportEmbed?reportId=${reportId}&appId=${appId}&autoAuth=true&ctid=${ctid}`;
    if (!base) return '';
    try {
      const urlObj = new URL(base);
      if (!urlObj.searchParams.has('filterPaneEnabled')) {
        urlObj.searchParams.set('filterPaneEnabled', 'true');
      }
      if (!urlObj.searchParams.has('navContentPaneEnabled')) {
        urlObj.searchParams.set('navContentPaneEnabled', 'true');
      }
      return urlObj.toString();
    } catch {
      let formatted = base;
      if (!formatted.includes('filterPaneEnabled=')) {
        formatted += (formatted.includes('?') ? '&' : '?') + 'filterPaneEnabled=true';
      }
      if (!formatted.includes('navContentPaneEnabled=')) {
        formatted += (formatted.includes('?') ? '&' : '?') + 'navContentPaneEnabled=true';
      }
      return formatted;
    }
  };

  const fallbackUrl = buildFallbackUrl(url);

  useEffect(() => {
    if (!isVisible || embedState !== 'idle') return;
    let cancelled = false;
    setEmbedState('loading');

    if (!reportId || !appId || !ctid) {
      setEmbedState('fallback');
      markLoaded();
      return;
    }

    const embed = async () => {
      try {
        const token = sessionStorage.getItem('access_token') ||
                      localStorage.getItem('access_token') ||
                      sessionStorage.getItem('auth_token') ||
                      localStorage.getItem('auth_token') ||
                      localStorage.getItem('token') || '';
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const url = `/api/v1/powerbi/embed-token/?app_id=${encodeURIComponent(appId)}&report_id=${encodeURIComponent(reportId)}&ctid=${encodeURIComponent(ctid || '')}`;
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 12000);
        const resp = await fetch(url, { headers, signal: controller.signal });
        window.clearTimeout(timeoutId);

        if (!resp.ok) {
          if (!cancelled) setEmbedState('fallback');
          markLoaded();
          return;
        }

        const text = await resp.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          if (!cancelled) setEmbedState('fallback');
          markLoaded();
          return;
        }
        // Backend says not configured or requested fallback → fall back to iframe
        if (!data.configured || !data.token || data.fallback) {
          if (!cancelled) setEmbedState('fallback');
          markLoaded();
          return;
        }

        if (cancelled || !containerRef.current) {
          return;
        }

        // 2. Embed using SDK
        const config: pbi.IEmbedConfiguration = {
          type: 'report',
          tokenType: pbi.models.TokenType.Embed,
          accessToken: data.token,
          embedUrl: data.embedUrl,
          id: reportId,
          permissions: pbi.models.Permissions.Read,
          settings: {
            filterPaneEnabled: true,
            navContentPaneEnabled: true,
            background: pbi.models.BackgroundType.Transparent,
            panes: {
              filters: {
                expanded: false,
                visible: true,
              },
            },
          },
        };

        const report = powerbiService.embed(containerRef.current, config) as pbi.Report;
        embedRef.current = report;

        report.on('loaded', () => {
          if (!cancelled) {
            setEmbedState('embedded');
            markLoaded();
          }
        });

        report.on('error', (event: any) => {
          const errorCode = event?.detail?.message || '';

          // Gateway unreachable is a data-source issue; keep embed mounted and show a notice.
          if (errorCode === 'DM_GWPipeline_Client_GatewayUnreachable') {
            if (!cancelled) {
              setGatewayIssue(true);
              setEmbedState('embedded');
            }
            markLoaded();
            return;
          }

          if (!cancelled) setEmbedState('fallback');
          markLoaded();

          if (process.env.NODE_ENV !== 'production') {
            console.error('[PowerBI] Report error:', errorCode || event?.detail || event);
          }
        });

      } catch (err) {
        if (!cancelled) setEmbedState('fallback');
        markLoaded();

        if (process.env.NODE_ENV !== 'production') {
          console.error('[PowerBI] Embed init failed:', err);
        }
      }
    };

    embed();

    return () => {
      cancelled = true;
    };
  }, [isVisible]); // eslint-disable-line

  // Clean up SDK embed on unmount
  useEffect(() => {
    return () => {
      if (embedRef.current && containerRef.current) {
        try {
          (embedRef.current as pbi.Report).off('loaded');
          (embedRef.current as pbi.Report).off('error');
          powerbiService.reset(containerRef.current);
        } catch {}
      }
    };
  }, []);

  // Do not crop the top of the iframe so dashboard headers are not cut off
  const HIDE_TRIAL_BANNER = false;
  const TRIAL_BANNER_HEIGHT = '37px';

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: isVisible ? 'block' : 'none',
  };

  const iframeStyle: React.CSSProperties = {
    border: 'none',
    position: 'absolute',
    top: HIDE_TRIAL_BANNER ? `-${TRIAL_BANNER_HEIGHT}` : '0',
    left: 0,
    width: '100%',
    height: HIDE_TRIAL_BANNER ? `calc(100% + ${TRIAL_BANNER_HEIGHT})` : '100%',
  };

  // Fallback: plain iframe with autoAuth=true (existing behaviour)
  if (embedState === 'fallback') {
    return (
      <div style={wrapperStyle}>
        <iframe
          title={title}
          src={fallbackUrl}
          frameBorder="0"
          allowFullScreen
          allow="fullscreen; clipboard-write; identity-credentials-get"
          onLoad={onLoad}
          style={iframeStyle}
        />
      </div>
    );
  }

  // SDK embed container (also used while loading)
  return (
    <div style={wrapperStyle}>
      {gatewayIssue && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 5,
            background: 'rgba(255, 243, 205, 0.96)',
            color: '#7a5b00',
            border: '1px solid #ffe08a',
            borderRadius: 6,
            padding: '8px 10px',
            fontSize: 12,
            fontWeight: 600,
            maxWidth: '70%',
          }}
        >
          Some Power BI visuals are temporarily unavailable (gateway unreachable).
        </div>
      )}
      <div
        ref={containerRef}
        title={title}
        style={iframeStyle}
      />
    </div>
  );
};

export default PowerBIEmbed;

